// The answer a forecast gives, in one shape every surface can print: the forecast header, the
// Forecast Manager cards, the phone layout. Pure (no React); every reading goes through the
// existing multiway helpers so a card and the timeline cannot name different leaders.
//
// The rules it encodes (PROTOTYPE.md "Numbers"):
//   • a yes/no question's headline names the side of 50% it is on (Yes at exactly 50%), so it
//     agrees with the emphasised row of the outcome readout; `pYes` keeps P(Yes);
//   • a multi-outcome question leads with its top NAMED outcome, and says "leads the named
//     field" (never "most likely") whenever a catch-all such as "Other" is larger;
//   • outcomes are listed named-first by probability, the catch-all last.

import {
  applyResolutionToDistribution,
  bucketLabel,
  extractMultiway,
  leadingRow,
  topNamedRow,
} from "./multiway.js";
import { fmtPct } from "./formatPct.js";
import { isVoidedContract } from "./contractStatus.js";

// Labels that mean "anything not listed", whether the space appended the implicit catch-all
// (flagged `isOther` by slot) or the question author wrote one into the outcome list.
const OTHER_LABEL =
  /^\s*(?:other\b|others\b|any other\b|anyone else\b|someone else\b|something else\b|none of the (?:above|listed|named)\b)/i;

/**
 * Is this outcome a catch-all ("Other", "Any other candidate", the implicit `allow_other` slot)?
 * @param {{label?: string, isOther?: boolean}|null|undefined} bucket
 * @returns {boolean}
 */
export function isOtherOutcome(bucket) {
  return bucket?.isOther === true || OTHER_LABEL.test(String(bucket?.label ?? ""));
}

/** "18%" — the shared percent rule (`fmtPct`) plus the sign. */
export const pctText = (p) => `${fmtPct(p)}%`;

/** P(Yes) as a 0–1 fraction, or null. The wire has carried both 0–1 and 0–100. */
function yesProbability(source) {
  const raw = source?.contract?.probability ?? source?.probability ?? null;
  if (typeof raw !== "number" || !Number.isFinite(raw)) return null;
  return raw > 1 ? raw / 100 : raw;
}

/**
 * The display order for a distribution: named outcomes by probability (an ordered or numeric
 * space keeps its own order, so a scale still reads as a scale), every catch-all last.
 * @param {Array<{label: string, p: number, isOther?: boolean}>} buckets from extractMultiway
 * @param {string} kind the outcome-space kind
 */
export function orderOutcomes(buckets, kind) {
  const rows = Array.isArray(buckets) ? buckets : [];
  const named = rows.filter((b) => !isOtherOutcome(b));
  const other = rows.filter((b) => isOtherOutcome(b));
  const keepOrder = kind === "numeric";
  const sortedNamed = keepOrder ? named : [...named].sort((a, b) => (b.p || 0) - (a.p || 0));
  return [...sortedNamed, ...other];
}

// A label long enough to read as a clause ("No Disney+ original series wins") gets a colon
// form ("Most likely: …") rather than being made the subject of a sentence.
const isClauseLike = (label) => label.length > 32 || label.trim().split(/\s+/).length >= 5;

/**
 * The sentence that names the leader.
 *  • the catch-all outweighs every named outcome together → "An outcome not listed is most likely"
 *  • a catch-all is larger than the leader → "JD Vance leads the named field"
 *  • otherwise → "JD Vance is most likely"
 * @param {{label: string}} leader
 * @param {{otherLeads: boolean, otherLarger: boolean}} flags
 */
export function leaderStatementOf(leader, { otherLeads = false, otherLarger = false } = {}) {
  if (!leader) return null;
  if (otherLeads) return "An outcome not listed is most likely";
  const label = String(leader.label ?? "");
  if (otherLarger)
    return isClauseLike(label)
      ? `Leading named outcome: ${label}`
      : `${label} leads the named field`;
  return isClauseLike(label) ? `Most likely: ${label}` : `${label} is most likely`;
}

/**
 * The answer as one phrase, worded as the header words it: "97% chance of No" for a yes/no
 * question, "JD Vance 41%" for a distribution; null when there is no answer yet.
 * @param {ReturnType<typeof answerSummary>} a
 * @returns {string|null}
 */
export function answerText(a) {
  if (a?.kind === "binary") return `${a.headlineText} ${a.headlineLabel}`;
  if (a?.kind === "multi" && a.leader) return `${a.leader.label} ${a.headlineText}`;
  return null;
}

/** "Oct 1" this year, "Oct 1, 2025" otherwise. */
export function shortDate(iso, now = new Date()) {
  if (!iso) return "";
  const s = String(iso);
  const d = new Date(s.length === 10 ? `${s}T00:00:00` : s);
  if (Number.isNaN(d.getTime())) return "";
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

/**
 * The forecast before this one, when the source carries it: a shift verdict's
 * `prevProbability` first (the scheduled-fire path), else the newest manual snapshot in
 * `history` (each Update pushes the state it replaced). Null when there is nothing to compare.
 * @param {any} source a run / forecast row
 * @returns {{p: number|null, at: string|null, contract: object|null}|null}
 */
export function previousForecast(source) {
  const shiftPrev = source?.shift?.prevProbability;
  if (typeof shiftPrev === "number" && Number.isFinite(shiftPrev)) {
    return { p: shiftPrev, at: source.shift.prevAt ?? null, contract: null };
  }
  const history = Array.isArray(source?.history) ? source.history : [];
  for (let i = history.length - 1; i >= 0; i--) {
    const h = history[i];
    const c = h?.contract;
    if (!c) continue;
    const p =
      typeof c.probability === "number" && Number.isFinite(c.probability) ? c.probability : null;
    if (p != null || c.forecast_value) return { p, at: h.generatedAt ?? null, contract: c };
  }
  return null;
}

/**
 * How far the headline number moved since the previous forecast.
 * @returns {{deltaPts: number, direction: "up"|"down"|"flat", text: string, since: string}|null}
 */
function changeOf(nowP, prevP, prevAt, now) {
  if (typeof nowP !== "number" || typeof prevP !== "number") return null;
  const deltaPts = Math.round((nowP - prevP) * 100);
  const since = prevAt
    ? `from the previous forecast (${shortDate(prevAt, now)})`
    : "from the previous forecast";
  if (deltaPts === 0) return { deltaPts: 0, direction: "flat", text: "No change", since };
  const n = Math.abs(deltaPts);
  return {
    deltaPts,
    direction: deltaPts > 0 ? "up" : "down",
    // U+2212 MINUS SIGN, so "+4" and "−4" take the same width in tabular figures.
    text: `${deltaPts > 0 ? "+" : "−"}${n} ${n === 1 ? "pt" : "pts"}`,
    since,
  };
}

const RUNNING = new Set(["running", "preprocessing"]);

/** The run-state chip, or null for a settled, live forecast. */
function statusOf(source, { verdict, resolvesAt, now }) {
  if (source?._pending || RUNNING.has(source?.status))
    return { kind: "running", label: "Forecasting" };
  if (source?.status === "errored") return { kind: "error", label: "Did not finish" };
  if (source?.status === "cancelled") return { kind: "error", label: "Cancelled" };
  if (verdict) return verdict;
  if (resolvesAt && resolvesAt < now) return { kind: "pending", label: "No answer yet" };
  return null;
}

/**
 * How a resolved forecast fared: what happened, and whether Sayer's final forecast called it
 * (yes/no: the side of 50% it was on; multi-outcome: the outcome it led with). Null while open.
 * @param {ReturnType<typeof answerSummary>} a
 * @returns {null | {outcome: string|null, called: boolean|null, rank: number|null, pText: string|null}}
 */
export function resolvedVerdict(a) {
  if (!a?.resolved) return null;
  const won = (a.outcomes || []).find((o) => o.isResolved) || null;
  if (!won) return { outcome: null, called: null, rank: null, pText: null };
  if (a.kind === "binary") {
    const pYes = a.pYes;
    const called =
      typeof pYes !== "number" || pYes === 0.5 ? null : pYes > 0.5 === (won.label === "Yes");
    return { outcome: won.label, called, rank: null, pText: won.pText };
  }
  const rank = [...a.outcomes].sort((x, y) => y.p - x.p).indexOf(won) + 1;
  return { outcome: won.label, called: !!won.isLeader, rank, pText: won.pText };
}

/** "2027-06-09" or an ISO instant → a local Date (a bare date is local midnight), or null. */
function resolutionMoment(iso) {
  if (!iso) return null;
  const s = String(iso);
  const d = new Date(s.length === 10 ? `${s}T23:59:59` : s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * The answer a forecast gives, ready to print.
 *
 * @param {any} source a forecast run / row ({contract, resolution, status, history, …}), or a
 *   bare contract. Pass the DISPLAYED run (scrub-aware) so the answer follows the timeline.
 * @param {{previous?: {p: number|null, at: string|null, contract?: object|null}|null, now?: Date}} [opts]
 *   `previous` overrides `previousForecast(source)` (e.g. a vintage the caller already holds).
 * @returns {{
 *   kind: "binary"|"multi"|"none",
 *   pYes: number|null,
 *   headlineValue: number|null,
 *   headlineText: string|null,
 *   headlineLabel: string|null,
 *   leader: {label: string, p: number, isOther: boolean}|null,
 *   leaderStatement: string|null,
 *   otherLarger: boolean,
 *   otherLeads: boolean,
 *   outcomes: Array<{label: string, p: number, pText: string, isOther: boolean, isLeader: boolean, isResolved: boolean}>,
 *   spaceKind: string|null,
 *   change: {deltaPts: number, direction: string, text: string, since: string}|null,
 *   status: {kind: string, label: string}|null,
 *   resolved: boolean,
 * }}
 *   `outcomes` is the display list: for a yes/no question, Yes then No; for a multi-outcome
 *   question, named outcomes by probability then the catch-all. `isLeader` marks the row the
 *   headline names. `pYes` is P(Yes) on a yes/no question, null otherwise.
 */
export function answerSummary(source, { previous, now = new Date() } = {}) {
  const prev = previous === undefined ? previousForecast(source) : previous;
  const resolution = source?.resolution ?? null;
  const status = resolution?.status ?? null;
  const voided = !!source && isVoidedContract(source);
  const resolvesAt = resolutionMoment(source?.contract?.resolution_date);
  const empty = {
    kind: "none",
    pYes: null,
    headlineValue: null,
    headlineText: null,
    headlineLabel: null,
    leader: null,
    leaderStatement: null,
    otherLarger: false,
    otherLeads: false,
    outcomes: [],
    spaceKind: null,
    change: null,
    resolved: false,
  };

  const mw = extractMultiway(source);
  if (mw) {
    const settled = applyResolutionToDistribution(mw, {
      status: voided ? "voided" : status,
      resolution,
    });
    const resolvedRow =
      typeof settled.resolvedIndex === "number" ? mw.buckets[settled.resolvedIndex] : null;
    // Leader from the FORECAST masses (not the 100/0 collapse), so the header keeps saying what
    // was forecast while the status chip says what happened.
    const named = mw.buckets.filter((b) => !isOtherOutcome(b));
    const other = mw.buckets.filter((b) => isOtherOutcome(b));
    const otherMass = other.reduce((s, b) => s + (b.p || 0), 0);
    const namedMass = named.reduce((s, b) => s + (b.p || 0), 0);
    // `leadingRow` already applies the "catch-all leads only if it outweighs every named row
    // together" rule for the implicit slot; an authored catch-all gets the same test here.
    const top = named.length ? topNamedRow(named) : leadingRow(mw.buckets);
    const otherLeads = other.length > 0 && otherMass > namedMass;
    const leaderRow = otherLeads
      ? other.length === 1
        ? other[0]
        : { label: "Other", p: otherMass, isOther: true }
      : top;
    const otherLarger = !otherLeads && other.some((b) => (b.p || 0) > (top?.p || 0));
    const leader = leaderRow
      ? { label: bucketLabel(leaderRow), p: leaderRow.p || 0, isOther: isOtherOutcome(leaderRow) }
      : null;
    const outcomes = orderOutcomes(mw.buckets, mw.kind).map((b) => ({
      label: bucketLabel(b),
      p: b.p || 0,
      pText: pctText(b.p),
      isOther: isOtherOutcome(b),
      isLeader: b === leaderRow,
      isResolved: b === resolvedRow,
    }));
    let change = null;
    if (prev?.contract && leaderRow) {
      const before = extractMultiway(prev.contract);
      const same = before?.buckets.find((b) => bucketLabel(b) === bucketLabel(leaderRow));
      if (same) change = changeOf(leaderRow.p, same.p, prev.at, now);
    }
    const verdict = voided
      ? { kind: "voided", label: "Voided" }
      : resolvedRow
        ? { kind: "resolved", label: `Resolved: ${bucketLabel(resolvedRow)}` }
        : settled.badge === "Tied"
          ? { kind: "resolved", label: "Resolved: tie" }
          : settled.badge === "Resolved"
            ? { kind: "resolved", label: "Resolved" }
            : null;
    return {
      ...empty,
      kind: "multi",
      headlineValue: leader?.p ?? null,
      headlineText: leader ? pctText(leader.p) : null,
      headlineLabel: leader?.label ?? null,
      leader,
      leaderStatement: leaderStatementOf(leader, { otherLeads, otherLarger }),
      otherLarger,
      otherLeads,
      outcomes,
      spaceKind: mw.kind,
      change,
      status: statusOf(source, { verdict, resolvesAt, now }),
      resolved: !!verdict && verdict.kind === "resolved",
    };
  }

  const pYes = yesProbability(source);
  const verdict = voided
    ? { kind: "voided", label: "Voided" }
    : status === "resolved_yes"
      ? { kind: "resolved", label: "Resolved: Yes" }
      : status === "resolved_no"
        ? { kind: "resolved", label: "Resolved: No" }
        : status === "resolved_tie"
          ? { kind: "resolved", label: "Resolved: tie" }
          : null;
  if (pYes == null) return { ...empty, status: statusOf(source, { verdict, resolvesAt, now }) };
  const yesLeads = pYes >= 0.5;
  const outcomes = [
    {
      label: "Yes",
      p: pYes,
      pText: pctText(pYes),
      isOther: false,
      isLeader: yesLeads,
      isResolved: status === "resolved_yes",
    },
    {
      label: "No",
      p: 1 - pYes,
      pText: pctText(1 - pYes),
      isOther: false,
      isLeader: !yesLeads,
      isResolved: status === "resolved_no",
    },
  ];
  const lead = outcomes[yesLeads ? 0 : 1];
  const prevP = prev?.p;
  return {
    ...empty,
    kind: "binary",
    pYes,
    headlineValue: lead.p,
    headlineText: lead.pText,
    headlineLabel: `chance of ${lead.label}`,
    leader: { label: lead.label, p: lead.p, isOther: false },
    outcomes,
    spaceKind: "binary",
    // The move of the side named now, so a forecast that crossed 50% shows how far it came.
    change: changeOf(lead.p, yesLeads || prevP == null ? prevP : 1 - prevP, prev?.at ?? null, now),
    status: statusOf(source, { verdict, resolvesAt, now }),
    resolved: !!verdict && verdict.kind === "resolved",
  };
}
